import { Button, Icon } from '@risksmart-app/atomic-ui';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface CopyButtonProps {
  value: string;
  copyLabel: string;
  copiedLabel: string;
  className?: string;
}

/**
 * Small copy-to-clipboard button built on the atomic-ui Button + navigator
 * clipboard API. Shows a transient "Copied" state with a check icon.
 * `className` is merged onto the button so the same control works on both the
 * light URL row and the dark code-snippet header bar.
 */
export const CopyButton: FC<CopyButtonProps> = ({
  value,
  copyLabel,
  copiedLabel,
  className,
}) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleCopy = useCallback(() => {
    void navigator.clipboard?.writeText(value);
    setCopied(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), 1600);
  }, [value]);

  return (
    <Button
      type={'button'}
      variant={'neutral'}
      style={'outline'}
      size={'sm'}
      className={className}
      onClick={handleCopy}
    >
      {copied ? copiedLabel : copyLabel}
      {copied && <Icon name={'check-2'} size={'sm'} />}
    </Button>
  );
};
